import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { TabId, tabElementId, tabPanelId } from '../tabIds';
import { GroupColors } from './groupColors';

interface IGroupTabBarProps {
  tabs: TabId[];
  active: TabId;
  onSelect: (tab: TabId) => void;
}

/**
 * Horizontally scrollable tab strip — the `overflow-x: auto` keeps every tab
 * reachable on viewports too narrow to show all seven in one row (Req 13.1),
 * instead of clipping the overflowing tabs.
 */
const TabList = styled.div`
  display: flex;
  align-items: stretch;
  gap: 4px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  border-bottom: 1px solid ${GroupColors.border};
  margin: 16px 0 4px;
`;

const Tab = styled.button<{ active: boolean }>`
  flex: 0 0 auto;
  white-space: nowrap;
  border: 0;
  background: transparent;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ active }) =>
    active ? GroupColors.textPrimary : GroupColors.textMuted};
  border-bottom: 2px solid
    ${({ active }) => (active ? GroupColors.accent : 'transparent')};
  margin-bottom: -1px;
  transition: color 0.15s;

  &:hover {
    color: ${GroupColors.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${GroupColors.accent};
    outline-offset: -2px;
    border-radius: 4px;
  }
`;

/**
 * Resilient, accessible tab bar for the Group Dashboard (Req 13).
 *
 * Implements the WAI-ARIA tablist pattern with manual activation:
 *   - `role="tablist"` container, `role="tab"` items carrying `aria-selected`,
 *     `aria-controls` (the panel id) and an `id` so assistive tech can announce
 *     the active tab (Req 13.4).
 *   - Roving `tabindex`: only the active tab is in the tab sequence (`0`); the
 *     rest are `-1` so arrow keys, not Tab, move between tabs.
 *   - Arrow Left/Right move focus between tabs and wrap around; Home/End jump to
 *     the first/last tab (Req 13.2). Enter/Space activate the focused tab via
 *     `onSelect` (Req 13.3).
 *   - The active tab is scrolled into view whenever it changes so a
 *     programmatic tab switch (e.g. from an alert) is never left off-screen.
 */
export const GroupTabBar = memo(
  ({ tabs, active, onSelect }: IGroupTabBarProps) => {
    const { t } = useTranslation();
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

    const focusTabAt = useCallback((index: number) => {
      tabRefs.current[index]?.focus();
    }, []);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        const { length } = tabs;
        if (length === 0) return;

        switch (event.key) {
          case 'ArrowRight':
            event.preventDefault();
            focusTabAt((index + 1) % length);
            break;
          case 'ArrowLeft':
            event.preventDefault();
            focusTabAt((index - 1 + length) % length);
            break;
          case 'Home':
            event.preventDefault();
            focusTabAt(0);
            break;
          case 'End':
            event.preventDefault();
            focusTabAt(length - 1);
            break;
          case 'Enter':
          case ' ':
          case 'Spacebar':
            event.preventDefault();
            onSelect(tabs[index]);
            break;
          default:
            break;
        }
      },
      [tabs, focusTabAt, onSelect],
    );

    // Keep the active tab visible when it changes (e.g. switched from an alert
    // or a narrow, scrolled strip). `scrollIntoView` is absent under jsdom, so
    // guard the call to keep tests environment-safe.
    useEffect(() => {
      const index = tabs.indexOf(active);
      const node = index >= 0 ? tabRefs.current[index] : null;
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }, [active, tabs]);

    return (
      <TabList role="tablist">
        {tabs.map((tabId, index) => {
          const isActive = tabId === active;
          return (
            <Tab
              key={tabId}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              id={tabElementId(tabId)}
              type="button"
              role="tab"
              active={isActive}
              aria-selected={isActive}
              aria-controls={tabPanelId(tabId)}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onSelect(tabId)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              {t(`pages.groupDetail.tabs.${tabId}`)}
            </Tab>
          );
        })}
      </TabList>
    );
  },
);
